import { useState } from "react";
import useWindowSize from "../hooks/useWindowSize";
import { authAPI } from "../services/api";

const styles = {
  body: {
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: "#EEF2FF",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    backgroundImage:
      "radial-gradient(ellipse at 30% 20%, rgba(59,91,219,0.15) 0%, transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(66,99,235,0.1) 0%, transparent 55%)",
  },
  brand: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
  },
  brandLogo: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "26px",
    letterSpacing: "4px",
    color: "#1E2A3B",
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  brandDash: { color: "var(--accent)", fontWeight: 300 },
  brandDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "var(--accent)",
    display: "inline-block",
    margin: "0 2px 8px",
  },
  card: {
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: "40px 44px",
    width: "100%",
    maxWidth: "460px",
    boxShadow: "0 8px 40px rgba(59,91,219,0.12), 0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59,91,219,0.15)",
  },
  heading: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "28px",
    fontWeight: 700,
    color: "#1A202C",
    marginBottom: "28px",
    textAlign: "center",
    letterSpacing: "-0.5px",
  },
  field: { marginBottom: "18px" },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#718096",
    marginBottom: "7px",
    letterSpacing: "0.2px",
  },
  input: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: "12px",
    border: "1.5px solid #DDE1E7",
    backgroundColor: "#F0F2F5",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "15px",
    color: "#1A202C",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: "var(--accent)",
    boxShadow: "0 0 0 3px rgba(59,91,219,0.08)",
    backgroundColor: "var(--accent-bg)",
  },
  btnPrimary: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)",
    color: "#ffffff",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "15px",
    fontWeight: 700,
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    marginTop: "22px",
    boxShadow: "0 4px 28px rgba(59,91,219,0.3)",
    letterSpacing: "0.2px",
    transition: "opacity 0.2s, transform 0.15s, box-shadow 0.2s",
  },
  dividerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: "20px 0",
    color: "#C8CDD6",
    fontSize: "12px",
    letterSpacing: "1px",
    fontWeight: 500,
  },
  dividerLine: { flex: 1, height: "1px", backgroundColor: "#DDE1E7" },
  btnSSO: {
    width: "100%",
    padding: "13px",
    backgroundColor: "transparent",
    color: "#2D3748",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "15px",
    fontWeight: 600,
    border: "1.5px solid #C8CDD6",
    borderRadius: "12px",
    cursor: "pointer",
    letterSpacing: "0.2px",
    transition: "background 0.2s, border-color 0.2s",
  },
  footerNote: {
    textAlign: "center",
    fontSize: "12px",
    color: "#718096",
    marginTop: "24px",
    lineHeight: 1.7,
  },
  footerLink: { color: "var(--accent)", textDecoration: "none", fontWeight: 500 },
  errorBox: {
    backgroundColor: "#FFF5F5",
    border: "1.5px solid #7f1d1d",
    borderRadius: "10px",
    padding: "10px 14px",
    marginBottom: "16px",
    fontSize: "13px",
    color: "#C53030",
    fontWeight: 500,
  },
};

export default function SignIn({ onSignIn }) {
  const size = useWindowSize();
  const isMobile = size < 480;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getInputStyle = (field) => ({
    ...styles.input,
    ...(focusedField === field ? styles.inputFocus : {}),
  });

  const handleSignIn = async () => {
    if (loading) return;
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const data = await authAPI.login(email.trim(), password);
      onSignIn?.(data.token, data.user);
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:wght@600&display=swap"
        rel="stylesheet"
      />
      <div style={styles.body}>
        <div style={{ ...styles.card, padding: isMobile ? "30px 24px" : "40px 44px" }}>
          <h1 style={styles.heading}>Sign in to PowerDesk</h1>
          <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.55, margin: "0 0 20px", textAlign: "center" }}>
            Use your <strong>ticket system</strong> email and password from IT.
            This is <strong>not</strong> your Microsoft 365 / Outlook sign-in.
          </p>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.field}>
            <label style={styles.label}>PowerDesk email (username)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              style={getInputStyle("email")}
              placeholder="you@company.com"
              autoComplete="email"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>PowerDesk password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              onFocus={() => setFocusedField("password")}
              onBlur={() => setFocusedField(null)}
              style={getInputStyle("password")}
              placeholder="••••••••••"
              autoComplete="current-password"
            />
          </div>

          <button
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            style={{ ...styles.btnPrimary, opacity: loading ? 0.8 : 1 }}
          >
            {loading ? "Signing in…" : "Sign in to PowerDesk"}
          </button>

        </div>
      </div>
    </>
  );
}
